import React, { useContext, useRef, useState } from 'react';
import Button from '../Button';
import { LanguageContext } from '../LanguageContext';


const FormField: React.FC<{ label?: string; name: string; type?: string; placeholder?: string; required?: boolean; as?: 'input' | 'textarea', value: string, onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void }> = ({ label, name, type = 'text', placeholder, required = false, as = 'input', value, onChange }) => {
    const commonProps = {
        name,
        id: name,
        placeholder,
        required,
        value,
        onChange,
        className: "block w-full px-4 py-3 border border-gray-300 dark:border-gray-700 rounded-md shadow-sm placeholder-gray-700 dark:placeholder-gray-300 focus:outline-none focus:ring-accent focus:border-accent sm:text-sm bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-50"
    };
    return (
        <div>
            {label && <label htmlFor={name} className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{label}</label>}
            {as === 'textarea' ? <textarea {...commonProps} rows={4} /> : <input type={type} {...commonProps} />}
        </div>
    );
}

const Contact: React.FC<{ openChat: (source?: string) => void }> = ({ openChat }) => {
    const { t, language } = useContext(LanguageContext);
    const formText = t?.contact?.form;
    const isSerbian = ['sr', 'sh', 'bs', 'hr'].includes(language.toLowerCase());

    const initialFormState = {
        name: '',
        company: '',
        email: '',
        phone: '',
        location: '',
        budget: formText?.budgetOptions?.[0] || 'Select a range',
        timeline: '',
        notes: '',
        website: '',
    };

    const [formData, setFormData] = useState(initialFormState);
    const [formStatus, setFormStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
    const [startedAt, setStartedAt] = useState(Date.now());
    const hasTrackedStart = useRef(false);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        if (!hasTrackedStart.current && e.target.name !== 'website') {
            hasTrackedStart.current = true;
            window.gtag?.('event', 'contact_form_start');
        }
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (formStatus === 'sending') return;
        setFormStatus('sending');

        try {
            const response = await fetch('/api/contact', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    type: 'project_brief',
                    ...formData,
                    language,
                    page: window.location.href,
                    startedAt,
                }),
            });

            if (!response.ok) throw new Error(`Contact request failed: ${response.status}`);

            setFormStatus('success');
            setFormData(initialFormState);
            setStartedAt(Date.now());
            hasTrackedStart.current = false;
            window.gtag?.('event', 'generate_lead', { lead_source: 'contact_form' });
            window.gtag?.('event', 'contact_form_submit');
        } catch (error) {
            console.error(error);
            setFormStatus('error');
        }
    };

    return (
        <section id="contact" className="py-20 sm:py-32 bg-white dark:bg-black scroll-mt-20">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
                    <div className="lg:col-span-1">
                        <h2 className="text-3xl font-bold tracking-tight sm:text-4xl text-gray-900 dark:text-gray-50">{t?.contact?.title || ''}</h2>
                        <p className="mt-4 text-lg text-gray-600 dark:text-gray-400">
                            {t?.assistant?.initialMessage || t?.contact?.description || ''}
                        </p>
                        <div className="mt-8">
                            <Button type="button" onClick={() => openChat('contact_primary')} variant="primary" className="w-full sm:w-auto">
                                {t?.assistant?.title || 'AI Assistant'}
                            </Button>
                        </div>
                        <div className="mt-10 space-y-4 border-t border-gray-200 pt-8 dark:border-gray-800">
                            <h3 className="font-semibold text-gray-900 dark:text-gray-50">{t?.contact?.directContact?.title || ''}</h3>
                            <p><a href={`mailto:${t?.contact?.directContact?.email || ''}`} className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-50">{t?.contact?.directContact?.email || ''}</a></p>
                            <p><a href={t?.contact?.directContact?.whatsappUAE?.href || ''} data-contact-region="uae" className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-50">{t?.contact?.directContact?.whatsappUAE?.label || ''}</a></p>
                            <p><a href={t?.contact?.directContact?.whatsappEU?.href || ''} data-contact-region="eu" className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-50">{t?.contact?.directContact?.whatsappEU?.label || ''}</a></p>
                        </div>
                    </div>
                    <details className="lg:col-span-2 group bg-gray-50 dark:bg-gray-900/50 rounded-lg border border-gray-200 dark:border-gray-800">
                        <summary className="list-none cursor-pointer p-8 flex items-center justify-between gap-4 font-semibold text-gray-900 dark:text-gray-50 [&::-webkit-details-marker]:hidden focus:outline-none focus-visible:ring-2 focus-visible:ring-accent rounded-lg">
                            <span>{formText?.buttonSend || 'Send Brief'}</span>
                            <span className="flex h-9 w-9 items-center justify-center rounded-full border border-gray-300 dark:border-gray-700 group-open:bg-accent group-open:text-accent-foreground group-open:border-accent" aria-hidden="true">
                                <svg className="h-5 w-5 transition-transform group-open:rotate-45" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M10 4v12M4 10h12" strokeLinecap="round" />
                                </svg>
                            </span>
                        </summary>
                        <form onSubmit={handleSubmit} className="grid grid-cols-1 sm:grid-cols-2 gap-6 px-8 pb-8 border-t border-gray-200 dark:border-gray-800 pt-8">
                            <FormField label={formText?.name} name="name" placeholder={formText?.namePlaceholder} required value={formData.name} onChange={handleChange} />
                            <FormField label={formText?.company} name="company" placeholder={formText?.companyPlaceholder} value={formData.company} onChange={handleChange} />
                            <FormField label={formText?.email} name="email" type="email" placeholder={formText?.emailPlaceholder} required value={formData.email} onChange={handleChange} />
                            <FormField label={formText?.phone} name="phone" type="tel" placeholder={formText?.phonePlaceholder} value={formData.phone} onChange={handleChange} />
                            <div className="sm:col-span-2">
                                <FormField label={formText?.location} name="location" placeholder={formText?.locationPlaceholder} value={formData.location} onChange={handleChange} />
                            </div>
                            <div>
                                <label htmlFor="budget" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{formText?.budget}</label>
                                <select id="budget" name="budget" value={formData.budget} onChange={handleChange} className="block w-full px-4 py-3 border border-gray-300 dark:border-gray-700 rounded-md shadow-sm focus:outline-none focus:ring-accent focus:border-accent sm:text-sm bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-300">
                                    {(formText?.budgetOptions || []).map((option: string) => <option key={option}>{option}</option>)}
                                </select>
                            </div>
                            <div>
                                <label htmlFor="timeline" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{formText?.timeline}</label>
                                <input type="text" name="timeline" id="timeline" placeholder={formText?.timelinePlaceholder} value={formData.timeline} onChange={handleChange} className="block w-full px-4 py-3 border border-gray-300 dark:border-gray-700 rounded-md shadow-sm placeholder-gray-700 dark:placeholder-gray-300 focus:outline-none focus:ring-accent focus:border-accent sm:text-sm bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-50" />
                            </div>
                            <div className="sm:col-span-2">
                                <FormField as="textarea" label={formText?.notes} name="notes" placeholder={formText?.notesPlaceholder} required value={formData.notes} onChange={handleChange} />
                            </div>
                            <label className="absolute -left-[10000px] top-auto h-px w-px overflow-hidden" aria-hidden="true">
                                Website
                                <input name="website" value={formData.website} onChange={handleChange} tabIndex={-1} autoComplete="off" />
                            </label>
                            <div className="sm:col-span-2 flex flex-col sm:flex-row gap-4 items-center">
                                <Button type="submit" variant="primary" className="w-full sm:w-auto" disabled={formStatus === 'sending'}>
                                    {formStatus === 'sending' ? (isSerbian ? 'Šaljem...' : 'Sending...') : (formText?.buttonSend || 'Send Brief')}
                                </Button>
                                <Button type="button" onClick={() => openChat('contact_form_secondary')} variant="secondary" className="w-full sm:w-auto">{t?.assistant?.title || 'AI Assistant'}</Button>
                            </div>
                            {formStatus === 'success' && (
                                <p className="sm:col-span-2 text-green-600 dark:text-green-400" role="status">
                                    {isSerbian ? 'Brif je uspešno poslat. Potvrda stiže na vaš email.' : 'Your brief has been sent. A confirmation is on its way to your email.'}
                                </p>
                            )}
                            {formStatus === 'error' && (
                                <p className="sm:col-span-2 text-red-600 dark:text-red-400" role="alert">
                                    {isSerbian ? 'Poruka nije poslata. Pokušajte ponovo ili pišite na info@buildhub.global.' : 'The message was not sent. Please try again or email info@buildhub.global.'}
                                </p>
                            )}
                        </form>
                    </details>
                </div>
            </div>
        </section>
    );
};

export default Contact;
